/**
 * The Help pane's conversation (System context PR B,
 * design/2026-10-14-help-agent-tours.md).
 *
 * One turn is POST /help/:customer/chat with the section and route on
 * screen, what the page describes of itself, and the tour step when a tour
 * is on. The server writes the system prompt from the feature registry,
 * filtered to the caller's role. The model's ui_search / ui_call run here
 * over the Help bridge (help/helpBridge): the mounted page's tools only,
 * costly and click-confirmed ones behind the pane's Confirm, through the
 * same loop and confirm rules as the Tables Insights pane
 * (components/chat/uiToolLoop).
 *
 * The conversation lives in the app-level HelpProvider, so it survives
 * moving between pages; each turn is answered for the page on screen then.
 */
import { useRef, useState } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import { extractErrorMessage } from '../utils/apitools'
import type { ChatMessage } from '../components/chat/types'
import { summarizeUiCalls, type UiCallResolution, type UiCallView } from '../components/chat/uiCalls'
import { runUiToolLoop } from '../components/chat/uiToolLoop'
import type { UIBridge } from '../voice/uiBridge'
import { sectionForRoute } from '../help/features'
import type { FeatureSection } from '../help/features'

/** The tour step on screen, as the help route takes it. */
export interface HelpTourContext {
  name: string
  /** 1-based. */
  step: number
  total: number
  feature?: string
  control?: string
  say?: string
  type?: string
  /** What the step's action just did on screen (Help pane live tours). */
  did?: string
}

export interface UseHelpChatOptions {
  customer: string
  job: string
  /** The route name on screen. */
  route: string
  bridge: UIBridge
  /** What the mounted page describes of itself now. */
  pageContext: () => string
  /** The tour step on screen, read when a turn is sent. */
  tour: () => HelpTourContext | undefined
}

export interface UseHelpChatReturn {
  /** The registry section for the route on screen, if it has one. */
  section: FeatureSection | undefined
  messages: ChatMessage[]
  isLoading: boolean
  error: string | null
  /** Send the person's message. */
  send: (text: string) => Promise<void>
  /** Ask for the tour step on screen to be explained. `label` is the line shown as the person's. */
  explainStep: (label: string) => Promise<void>
  reset: () => void
  /** Add a line from the pane itself (not the model), e.g. what "Send to the agent" did. */
  addNote: (content: string) => void
  uiCalls: Record<string, UiCallView>
  confirmUiCall: (callId: string) => Promise<void>
  cancelUiCall: (callId: string) => void
}

export function useHelpChat({ customer, job, route, bridge, pageContext, tour }: UseHelpChatOptions): UseHelpChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resolutions, setResolutions] = useState<Record<string, UiCallResolution>>({})
  // The ui_call ids this pane ran: only those may be confirmed.
  const [sessionCalls, setSessionCalls] = useState<Record<string, true>>({})
  const decidedRef = useRef<Set<string>>(new Set())
  // Read at send time: a turn is answered for the page and section on screen then.
  const routeRef = useRef(route)
  routeRef.current = route
  const messagesRef = useRef(messages)
  messagesRef.current = messages

  const section = sectionForRoute(route)
  const uiCalls = summarizeUiCalls(messages, resolutions, { actionable: (id) => !!sessionCalls[id] })

  // The section and route the last turn was answered for. A turn that took
  // the person to a page with no help of its own (navigate_to_page) finishes
  // there, answered for where it started.
  const answeredForRef = useRef<{ section: FeatureSection; route: string } | null>(null)

  const postChat = async (conversation: ChatMessage[], note?: string): Promise<ChatMessage[]> => {
    const onScreen = sectionForRoute(routeRef.current)
    const target = onScreen ? { section: onScreen, route: routeRef.current } : answeredForRef.current
    if (!target) throw new Error('There is no help for this page yet.')
    answeredForRef.current = target
    const { section: sec, route: here } = target
    const page = pageContext()
    const res = await axios.post<ChatMessage[]>(`${API_BASE_URL}/help/${encodeURIComponent(customer)}/chat`, {
      section: sec.key,
      route: here,
      job: job || undefined,
      messages: conversation,
      page_context: note ? `${page}\n\n${note}` : page,
      ui_bridge: true,
      tour: tour(),
    })
    return res.data
  }

  const turn = async (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || isLoading) return
    const previous = messagesRef.current
    const pending = [...previous, { role: 'user', content: trimmed }]
    // The furthest the turn got: page changes already made stay on screen.
    let reached = pending
    const progress = (c: ChatMessage[]) => {
      reached = c
      setMessages(c)
    }
    setMessages(pending)
    setIsLoading(true)
    setError(null)
    try {
      const first = await postChat(pending)
      progress(first)
      const done = await runUiToolLoop(first, {
        bridge,
        postChat,
        progress,
        onCallsRun: (ids) => setSessionCalls((prev) => ({ ...prev, ...Object.fromEntries(ids.map((id) => [id, true as const])) })),
      })
      setMessages(done)
    } catch (e) {
      setError(extractErrorMessage(e))
      setMessages(reached === pending ? previous : reached)
    } finally {
      setIsLoading(false)
    }
  }

  // The person's click on Confirm runs the change itself, with no model call.
  const confirmUiCall = async (callId: string) => {
    const view = uiCalls[callId]
    if (!view || view.status !== 'needs_confirmation' || decidedRef.current.has(callId)) return
    decidedRef.current.add(callId)
    setResolutions((prev) => ({ ...prev, [callId]: { status: 'confirming' } }))
    let result: Record<string, unknown> = {}
    try {
      result = JSON.parse(await bridge.confirm(view.confirmTool ?? view.tool, view.confirmArgs ?? view.args))
    } catch (e) {
      result = { error: e instanceof Error ? e.message : 'it did not go through' }
    }
    const ok = !result.error && !result.needs_confirmation
    const message = ok
      ? (typeof result.did === 'string' ? result.did : 'Done')
      : (typeof result.error === 'string' ? result.error : 'it did not go through')
    setResolutions((prev) => ({ ...prev, [callId]: { status: ok ? 'confirmed' : 'failed', message } }))
    setMessages((prev) => [...prev, { role: 'assistant', content: ok ? `${message}.` : `That did not go through: ${message}.` }])
  }

  const cancelUiCall = (callId: string) => {
    const view = uiCalls[callId]
    if (!view || view.status !== 'needs_confirmation' || decidedRef.current.has(callId)) return
    decidedRef.current.add(callId)
    setResolutions((prev) => ({ ...prev, [callId]: { status: 'cancelled' } }))
    setMessages((prev) => [...prev, { role: 'assistant', content: 'Cancelled. Nothing was changed.' }])
  }

  const reset = () => {
    setMessages([])
    setError(null)
    setResolutions({})
    setSessionCalls({})
    decidedRef.current = new Set()
  }

  // The ref moves at once, so a turn started straight after a note (a tour
  // step that could not act, then its explanation) keeps the note.
  const addNote = (content: string) => {
    const next = [...messagesRef.current, { role: 'assistant', content }]
    messagesRef.current = next
    setMessages(next)
  }

  return {
    section,
    messages,
    isLoading,
    error,
    send: turn,
    explainStep: turn,
    reset,
    addNote,
    uiCalls,
    confirmUiCall,
    cancelUiCall,
  }
}

export default useHelpChat
